import { PRICING_TIERS } from "@shared/billing-config";

declare global {
  interface Window {
    fbq?: ((...args: any[]) => void) & {
      callMethod?: (...args: any[]) => void;
      queue: any[];
      loaded: boolean;
      version: string;
      push: (...args: any[]) => void;
    };
    _fbq?: Window["fbq"];
    _linkedin_partner_id?: string;
    _linkedin_data_partner_ids?: string[];
  }
}

let fbInitialized = false;
let liInitialized = false;

export const initFacebookPixel = () => {
  try {
    const pixelId = import.meta.env.VITE_FACEBOOK_PIXEL_ID;
    if (!pixelId || fbInitialized) return;
    fbInitialized = true;

    if (window.fbq) return;

    const fbq: any = function (...args: any[]) {
      if (fbq.callMethod) {
        fbq.callMethod(...args);
      } else {
        fbq.queue.push(args);
      }
    };
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = "2.0";
    fbq.queue = [];
    window.fbq = fbq;
    window._fbq = fbq;

    const script = document.createElement("script");
    script.async = true;
    script.src = "https://connect.facebook.net/en_US/fbevents.js";
    document.head.appendChild(script);

    window.fbq!("init", pixelId);
  } catch {
    // Tracking must never interfere with page behavior.
  }
};

export const initLinkedInTag = () => {
  try {
    const partnerId = import.meta.env.VITE_LINKEDIN_PARTNER_ID;
    if (!partnerId || liInitialized) return;
    liInitialized = true;

    window._linkedin_partner_id = partnerId;
    window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
    window._linkedin_data_partner_ids.push(partnerId);

    const script = document.createElement("script");
    script.async = true;
    script.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
    document.head.appendChild(script);
  } catch {
    // Tracking must never interfere with page behavior.
  }
};

// ---------------------------------------------------------------------------
// Meta browser/CAPI events. All helpers fail soft so tracking can never break
// public-page navigation or form submissions.
// ---------------------------------------------------------------------------

/** Generate an event ID shared between the browser Pixel and the server-side
 * Conversions API so Meta can deduplicate the two into a single event. */
export const generateMetaEventId = (): string => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `evt-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
};

const readCookie = (name: string): string | undefined => {
  try {
    const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : undefined;
  } catch {
    return undefined;
  }
};

/** Meta browser cookies used to improve CAPI Event Match Quality. */
export const getMetaCookies = (): { fbp?: string; fbc?: string } => ({
  fbp: readCookie("_fbp"),
  fbc: readCookie("_fbc"),
});

export type MetaEventMetadata = {
  eventId: string;
  fbp?: string;
  fbc?: string;
  eventSourcePath: string;
};

/** Build the metadata shared by Pixel and the Conversions API for one event. */
export const createMetaEventMetadata = (eventId = generateMetaEventId()): MetaEventMetadata => ({
  eventId,
  ...getMetaCookies(),
  eventSourcePath: window.location.pathname,
});

/** Fire the browser half of a deduplicated Meta standard event. */
export const trackMetaStandardEvent = (eventName: string, eventId: string) => {
  try {
    if (window.fbq) {
      window.fbq("track", eventName, {}, { eventID: eventId });
    }
  } catch {
    // Tracking must never interfere with page behavior.
  }
};

/**
 * Best-effort browser-to-server CAPI dispatch. This intentionally never throws:
 * analytics availability must not change public-page behavior.
 */
export const sendMetaEvent = (eventName: string, metadata: MetaEventMetadata) => {
  try {
    fetch("/api/meta/events", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ eventName, ...metadata }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    // Tracking must never interfere with page behavior.
  }
};

/** Send matching browser Pixel and server CAPI events using one event ID. */
export const trackPairedMetaEvent = (eventName: string, metadata = createMetaEventMetadata()) => {
  trackMetaStandardEvent(eventName, metadata.eventId);
  sendMetaEvent(eventName, metadata);
  return metadata;
};

export const trackAdPageView = () => trackPairedMetaEvent("PageView");

export const trackMetaContact = () => trackPairedMetaEvent("Contact");

export const trackMetaLead = (eventId: string) => trackMetaStandardEvent("Lead", eventId);

/** Resolve delegated clicks on email/phone links, including clicks on child icons/text. */
export const findMetaContactAnchor = (target: EventTarget | null): HTMLAnchorElement | null => {
  if (!(target instanceof Element)) return null;
  return target.closest<HTMLAnchorElement>('a[href^="mailto:"], a[href^="tel:"]');
};

/** Honeypot fake-success responses explicitly opt out of browser Lead tracking. */
export const wasLeadAccepted = (result: unknown): boolean =>
  typeof result === "object" &&
  result !== null &&
  "leadAccepted" in result &&
  (result as { leadAccepted?: unknown }).leadAccepted === true;

/** "Start Free Trial" click — lightweight upper-funnel custom event.
 * Always sends value/currency for Meta data quality; defaults to the
 * lowest tier's monthly price from the shared billing config. */
export const trackStartTrialClick = (value?: number) => {
  if (window.fbq) {
    const defaultValue = PRICING_TIERS[0].monthlyPrice;
    const eventValue = typeof value === "number" && Number.isFinite(value) ? value : defaultValue;
    window.fbq("trackCustom", "StartTrialClick", {
      value: eventValue,
      currency: "USD",
    });
  }
};

/** Landing on the /signup page. */
export const trackInitiateCheckout = () => {
  if (window.fbq) {
    window.fbq("track", "InitiateCheckout");
  }
};

/** Successful Stripe card confirmation during embedded signup. */
export const trackAddPaymentInfo = () => {
  if (window.fbq) {
    window.fbq("track", "AddPaymentInfo");
  }
};

/** Primary sign_up conversion. Pass the same eventId sent to the server so
 * the Pixel event and the CAPI event deduplicate in Meta Events Manager. */
export const trackCompleteRegistration = (eventId: string) => {
  if (window.fbq) {
    window.fbq("track", "CompleteRegistration", {}, { eventID: eventId });
  }
};
